import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	ANALYTICS_SOURCE,
	ANALYTICS_EVENT_TYPE,
	ANALYTICS_TYPE,
	ANALYTICS_UI_ACTION,
	ANALYTICS_UI_SUBJECT,
	ANALYTICS_UI_SUBJECT_ID,
	ANALYTICS_OPERATIONAL_ACTION,
	type AnalyticsOrigin,
} from '../../constants/snippetAnalytics.ts';

import {
	buildSnippetUIEvent,
	mergeOriginIntoAttributes,
	type SnippetUIEventData,
} from './snippetAnalyticsPayload.ts';

type SnippetOperationalEventData = {
	actionSubject: string;
	action: string;
	attributes?: Record<string, unknown>;
};

type SnippetTrackEventData = {
	actionSubject: string;
	action: string;
	attributes?: Record<string, unknown>;
};

/**
 * Hook that provides pre-configured analytics helpers for the snippets package.
 *
 * Encapsulates `useAnalyticsEvents` and injects the shared source, event type,
 * and channel so callers only need to provide the event-specific fields.
 *
 * Shorthand helpers (fireButtonClickedAnalytic, fireModalClosedAnalytic, etc.)
 * cover the most common actionSubject + action combinations so callers only
 * need to supply the actionSubjectId and optional attributes.
 *
 * @param origin - Optional origin that is automatically included in every
 *   event's attributes. Per-call attributes with the same key take precedence.
 */
export const useSnippetAnalytics = (origin?: AnalyticsOrigin) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireUIAnalytic = (data: SnippetUIEventData) => {
		createAnalyticsEvent(buildSnippetUIEvent(data, origin)).fire();
	};

	const fireOperationalAnalytic = useCallback(
		(data: SnippetOperationalEventData) => {
			createAnalyticsEvent({
				type: ANALYTICS_EVENT_TYPE.SEND_OPERATIONAL_EVENT,
				data: {
					source: ANALYTICS_SOURCE,
					type: ANALYTICS_TYPE.OPERATIONAL,
					...data,
					attributes: mergeOriginIntoAttributes(origin, data.attributes),
				},
			}).fire();
		},
		[createAnalyticsEvent, origin],
	);

	const fireTrackAnalytic = (data: SnippetTrackEventData) => {
		createAnalyticsEvent({
			type: ANALYTICS_EVENT_TYPE.SEND_TRACK_EVENT,
			data: {
				source: ANALYTICS_SOURCE,
				...data,
				attributes: mergeOriginIntoAttributes(origin, data.attributes),
			},
		}).fire();
	};

	// ─── Shorthand helpers ──────────────────────────────────────────────

	const fireButtonClickedAnalytic = (
		actionSubjectId: string,
		attributes?: Record<string, unknown>,
	) => {
		fireUIAnalytic({
			actionSubject: ANALYTICS_UI_SUBJECT.BUTTON,
			action: ANALYTICS_UI_ACTION.CLICKED,
			actionSubjectId,
			attributes,
		});
	};

	/**
	 * Fires an Awareness (impression) event when a snippets surface renders.
	 * Shape: actionSubject=snippets, action=viewed, actionSubjectId=snippets.
	 * The surface is conveyed via the hook-level origin (e.g. snippetGallery,
	 * snippetPanel) which is merged into attributes automatically.
	 *
	 * Memoised with `useCallback` so its identity is stable across renders. This
	 * lets consumers list it as a `useEffect` dependency for fire-once-on-mount
	 * impressions without the effect re-running on every parent re-render.
	 */
	const fireScreenViewedAnalytic = useCallback(
		(attributes?: Record<string, unknown>) => {
			createAnalyticsEvent(
				buildSnippetUIEvent(
					{
						actionSubject: ANALYTICS_UI_SUBJECT.SNIPPETS,
						action: ANALYTICS_UI_ACTION.VIEWED,
						actionSubjectId: ANALYTICS_UI_SUBJECT_ID.SNIPPETS,
						attributes,
					},
					origin,
				),
			).fire();
		},
		[createAnalyticsEvent, origin],
	);

	const fireModalClosedAnalytic = (
		actionSubjectId: string,
		attributes?: Record<string, unknown>,
	) => {
		fireUIAnalytic({
			actionSubject: ANALYTICS_UI_SUBJECT.MODAL,
			action: ANALYTICS_UI_ACTION.CLOSED,
			actionSubjectId,
			attributes,
		});
	};

	const fireTextFieldTypedAnalytic = (
		actionSubjectId: string,
		attributes?: Record<string, unknown>,
	) => {
		fireUIAnalytic({
			actionSubject: ANALYTICS_UI_SUBJECT.TEXT_FIELD,
			action: ANALYTICS_UI_ACTION.TYPED,
			actionSubjectId,
			attributes,
		});
	};

	const fireDropdownItemClickedAnalytic = (
		actionSubjectId: string,
		attributes?: Record<string, unknown>,
	) => {
		fireUIAnalytic({
			actionSubject: ANALYTICS_UI_SUBJECT.DROPDOWN_ITEM,
			action: ANALYTICS_UI_ACTION.CLICKED,
			actionSubjectId,
			attributes,
		});
	};

	const fireFailedAnalytic = useCallback(
		(actionSubject: string, attributes?: Record<string, unknown>) => {
			fireOperationalAnalytic({
				actionSubject,
				action: ANALYTICS_OPERATIONAL_ACTION.FAILED,
				attributes,
			});
		},
		[fireOperationalAnalytic],
	);

	const fireSuccessAnalytic = (actionSubject: string, attributes?: Record<string, unknown>) => {
		fireOperationalAnalytic({
			actionSubject,
			action: ANALYTICS_OPERATIONAL_ACTION.SUCCEEDED,
			attributes,
		});
	};

	return {
		fireOperationalAnalytic,
		fireTrackAnalytic,
		fireButtonClickedAnalytic,
		fireScreenViewedAnalytic,
		fireModalClosedAnalytic,
		fireTextFieldTypedAnalytic,
		fireDropdownItemClickedAnalytic,
		fireFailedAnalytic,
		fireSuccessAnalytic,
	};
};
